// Anlegen/Bearbeiten einer Konzern-Gruppe (Name + Mitglieds-Gesellschaften +
// Eliminieren-Flag). Nutzt POST/PUT /liquidity-planning/consolidation/groups.

import { useEffect, useState } from 'react'
import { apiClient } from '@/lib/api.ts'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import type { Company, Group } from './konzern-shared.ts'

export function CreateGroupModal({
  open, onOpenChange, year, companies, group, onSaved,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  year: number
  companies: Company[]
  group: Group | null
  onSaved: (id: number) => void
}) {
  const [name, setName] = useState('Gesamtkonzern')
  const [members, setMembers] = useState<Set<string>>(new Set())
  const [eliminate, setEliminate] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (group) {
      setName(group.name)
      setMembers(new Set(group.memberKeys))
      setEliminate(group.eliminate)
    } else {
      setName('Gesamtkonzern')
      setMembers(new Set())
      setEliminate(true)
    }
    setError(null)
  }, [open, group])

  function toggle(key: string) {
    setMembers((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }

  async function handleSubmit() {
    setSubmitting(true)
    setError(null)
    try {
      const body = { name, year, memberKeys: Array.from(members), eliminate }
      if (group) {
        await apiClient.put(`/liquidity-planning/consolidation/groups/${group.id}`, body)
        onSaved(group.id)
      } else {
        const res = await apiClient.post<{ id: number }>('/liquidity-planning/consolidation/groups', body)
        onSaved(res.data.id)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={group ? 'Konzern-Gruppe bearbeiten' : 'Neue Konzern-Gruppe'}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting || !name || members.size === 0}>
            {submitting ? 'Speichere…' : group ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} hint={`Geschäftsjahr ${year}`} />
        <div>
          <label className="text-small font-medium text-gray-700 mb-1 block">Gesellschaften</label>
          <div className="space-y-1 max-h-72 overflow-y-auto border border-gray-200 rounded p-2">
            {companies.map((c) => (
              <Checkbox
                key={c.key}
                label={`${c.name} (${c.key})`}
                checked={members.has(c.key)}
                onChange={() => toggle(c.key)}
              />
            ))}
          </div>
        </div>
        <Checkbox
          label="Intercompany-Beziehungen im Konzern eliminieren"
          checked={eliminate}
          onChange={() => setEliminate((v) => !v)}
        />
        {error && <p className="text-small text-red-600">{error}</p>}
      </div>
    </Modal>
  )
}
